'use client';

/**
 * EditorialScheduleCall — compact "schedule a call" treatment for the editorial
 * (`se3`) invest sidebar.
 *
 * PD-36055 (Hannah): the shared `SEContact` card (52px icon, 16px copy, legacy
 * gradient) is oversized for the editorial sidebar. This is a smaller card in
 * the SE3 card vocabulary (cream tile, 36px teal icon ring, 12px copy). It
 * resolves the sales-agent calendar URL via the same `getSalesAgentCalendar`
 * query key as `SEContact`, so it shares the cache and adds no extra request.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';

import { se3Colors, se3FontFamily } from '@startengine/design-system.se3.tokens';
import { getSalesAgentCalendar } from 'services/user';
import { TEAL, MUTED_TEXT } from './editorialSection';

const DEFAULT_CALENDAR_URL = 'https://meetings-na2.hubspot.com/start-engine/startengine-meeting';
const LABEL = 'Schedule a call with an Associate';

const EditorialScheduleCall = ({ calendarUrlOverride = '' }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['salesAgentCalendar'],
    queryFn: () => getSalesAgentCalendar(),
    enabled: !calendarUrlOverride,
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const calendarUrl = calendarUrlOverride || data?.calendarUrl || DEFAULT_CALENDAR_URL;

  return (
    <Box
      data-cy="editorial-schedule-call"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '13px 16px',
        borderRadius: '12px',
        border: `1px solid ${se3Colors.edge}`,
        backgroundColor: se3Colors.cream2,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          backgroundColor: se3Colors.card,
          border: `1px solid ${se3Colors.edge}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HeadsetMicOutlinedIcon sx={{ fontSize: 18, color: TEAL }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: se3FontFamily.sans,
            fontSize: '12px',
            fontWeight: 600,
            color: se3Colors.ink,
            lineHeight: 1.3,
          }}
        >
          Need help?
        </Box>
        {!calendarUrlOverride && isLoading ? (
          <Skeleton width={150} height={14} data-cy="editorial-schedule-call-loading" />
        ) : (
          <Box
            component="a"
            href={calendarUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="editorial-schedule-call-link"
            sx={{
              fontFamily: se3FontFamily.sans,
              fontSize: '12px',
              fontWeight: 600,
              color: TEAL,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              display: 'inline-block',
            }}
          >
            {LABEL}
          </Box>
        )}
        <Box
          sx={{
            fontFamily: se3FontFamily.sans,
            fontSize: '10.5px',
            color: MUTED_TEXT,
            marginTop: '2px',
            letterSpacing: '0.02em',
          }}
        >
          9AM – 5PM PST · Mon – Fri
        </Box>
      </Box>
    </Box>
  );
};

EditorialScheduleCall.propTypes = {
  calendarUrlOverride: PropTypes.string,
};

export default EditorialScheduleCall;
